import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Typography, Tag, Tabs, Card, Button, Space, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ApplicationCell, DataTable, ExternalApplicationCell, PageLayout } from '../../ui';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useGetCompanyDriversQuery } from '../../store/api/driversVehiclesApi';
import { CompanyDriver } from '../../store/api/driversVehiclesApi';
import { formatResponsibleUserLabel } from '../../utils/userDisplayName';
import type { ResponsibleUserInput } from '../../utils/userDisplayName';
import { extractCityFromAddress } from '../../utils/addressUtils';
import { formatApplicationPrintLine } from '../../utils/applicationDisplayFormat';
import { vehicleCategoryLabel } from '../../constants/vehicleCategory';
import { FileTextOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useGetVehicleByIdQuery, useGetDriverByIdQuery } from '../../store/api/referencesApi';
import CreateDriverModal from '../../components/common/CreateDriverModal';
import CreateVehicleModal from '../../components/common/CreateVehicleModal';

const { Text } = Typography;
const { TabPane } = Tabs;

function resolveDriverLogist(record: CompanyDriver): ResponsibleUserInput | null | undefined {
  return (
    record.assignedLogist ||
    record.currentRoute?.responsibleLogist ||
    record.application?.responsibleLogist ||
    null
  );
}

const DriversVehiclesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'in_route' | 'idle'>('all');
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<any | null>(null);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState<number | null>(null);
  
  // Загружаем данные транспорта для редактирования
  const { data: vehicleData } = useGetVehicleByIdQuery(editingVehicleId!, {
    skip: !editingVehicleId
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const filterDriverId = useMemo(() => {
    const raw = searchParams.get('driverId');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [searchParams]);
  const openedDriverFromUrlRef = useRef<number | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role?.name === 'admin';

  const { data: driverFromDeepLink } = useGetDriverByIdQuery(filterDriverId!, {
    skip: !filterDriverId,
  });

  const { data: driverDetailsForModal } = useGetDriverByIdQuery(editingDriver?.id!, {
    skip: !driverModalOpen || editingDriver?.id == null,
  });

  useEffect(() => {
    if (filterDriverId == null || !driverFromDeepLink) return;
    if (openedDriverFromUrlRef.current === filterDriverId) return;
    openedDriverFromUrlRef.current = filterDriverId;
    setEditingDriver(driverFromDeepLink);
    setDriverModalOpen(true);
  }, [filterDriverId, driverFromDeepLink]);

  const { data: driversData, isLoading, refetch } = useGetCompanyDriversQuery({
    status:
      filterDriverId != null ? undefined : activeTab === 'all' ? undefined : activeTab,
  });

  const drivers = useMemo(() => driversData?.drivers ?? [], [driversData?.drivers]);

  const handleExportDriverText = (record: CompanyDriver) => {
    try {
      const route = record.currentRoute;
      const app = record.application;
      const from = route ? extractCityFromAddress(route.loadingAddress) : '—';
      const to = route ? extractCityFromAddress(route.unloadingAddress) : '—';
      const plannedLoad = route?.plannedLoadingDate
        ? dayjs(route.plannedLoadingDate).format('DD.MM.YYYY')
        : '—';
      const actualLoad = route?.actualLoadingDate
        ? dayjs(route.actualLoadingDate).format('DD.MM.YYYY')
        : '—';
      const plannedUnload = route?.plannedUnloadingDate
        ? dayjs(route.plannedUnloadingDate).format('DD.MM.YYYY')
        : '—';
      const actualUnload = route?.actualUnloadingDate
        ? dayjs(route.actualUnloadingDate).format('DD.MM.YYYY')
        : '—';

      const truckLine = record.defaultVehicle
        ? `Тягач: ${record.defaultVehicle.registrationNumber} (${vehicleCategoryLabel(record.defaultVehicle.vehicleCategory)})`
        : 'Тягач: не назначен';
      const trailerLine = record.defaultTrailerVehicle
        ? `Прицеп: ${record.defaultTrailerVehicle.registrationNumber} (${vehicleCategoryLabel(record.defaultTrailerVehicle.vehicleCategory)})`
        : 'Прицеп: не указан';
      const lines = [
        `Водитель: ${record.fullName}${record.phone ? `, тел. ${record.phone}` : ''}`,
        truckLine,
        trailerLine,
        '',
        route
          ? `Маршрут: ${from} → ${to} (${route.routeNumber})`
          : 'Маршрут: —',
        app
          ? formatApplicationPrintLine(app)
          : 'Заявка: —',
        '',
        `Дата погрузки: пл. ${plannedLoad}, ф. ${actualLoad}`,
        `Дата выгрузки: пл. ${plannedUnload}, ф. ${actualUnload}`,
        '',
        `Логист: ${formatResponsibleUserLabel(resolveDriverLogist(record))}`
      ];

      const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Водитель_${record.fullName.replace(/\s+/g, '_')}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('Ошибка текстового экспорта водителя', e);
      message.error('Не удалось сформировать текстовый файл');
    }
  };

  const handleOpenDriverCreate = () => {
    setEditingDriver(null);
    setDriverModalOpen(true);
  };

  const handleOpenDriverEdit = (record: CompanyDriver) => {
    // Собираем минимальный объект Driver для модалки создания/редактирования
    const driverStub = {
      id: record.id,
      fullName: record.fullName,
      phone: record.phone,
      passportData: '',
      driverLicense: '',
      isCompanyDriver: true,
      defaultVehicleId: record.defaultVehicle?.id,
      defaultTrailerVehicleId: record.defaultTrailerVehicle?.id,
      defaultVehicle: record.defaultVehicle,
      defaultTrailerVehicle: record.defaultTrailerVehicle
    };
    setEditingDriver(driverStub);
    setDriverModalOpen(true);
  };

  const handleOpenVehicleEdit = (vehicle: CompanyDriver['defaultVehicle'] | CompanyDriver['defaultTrailerVehicle']) => {
    if (!vehicle) return;
    setEditingVehicleId(vehicle.id);
    setVehicleModalOpen(true);
  };

  const columns: ColumnsType<CompanyDriver> = [
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      fixed: 'left',
      render: (text: string, record: CompanyDriver) => (
        <div>
          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Link
              to={`/drivers/${record.id}`}
              state={{ backgroundLocation: location }}
              onClick={(e) => e.stopPropagation()}
            >
              {text}
            </Link>
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleExportDriverText(record);
              }}
            />
            {isAdmin && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDriverEdit(record);
                }}
              />
            )}
          </div>
          {record.phone && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.phone}</div>
          )}
        </div>
      )
    },
    {
      title: 'Тягач / прицеп',
      key: 'vehicle',
      width: 220,
      render: (_: unknown, record: CompanyDriver) => (
        <Space direction="vertical" size={2}>
          {record.defaultVehicle ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  <Text strong>{record.defaultVehicle.registrationNumber}</Text>
                  <Text type="secondary"> · {vehicleCategoryLabel(record.defaultVehicle.vehicleCategory)}</Text>
                </span>
                {isAdmin && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenVehicleEdit(record.defaultVehicle);
                    }}
                  />
                )}
              </div>
              {record.defaultVehicle.brand && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.defaultVehicle.brand}</div>
              )}
            </div>
          ) : (
            <Text type="secondary">Тягач не назначен</Text>
          )}
          {record.defaultTrailerVehicle ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  <Text strong>{record.defaultTrailerVehicle.registrationNumber}</Text>
                  <Text type="secondary"> · {vehicleCategoryLabel(record.defaultTrailerVehicle.vehicleCategory)}</Text>
                </span>
                {isAdmin && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenVehicleEdit(record.defaultTrailerVehicle);
                    }}
                  />
                )}
              </div>
              {record.defaultTrailerVehicle.brand && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.defaultTrailerVehicle.brand}</div>
              )}
            </div>
          ) : (
            <Text type="secondary">Прицеп не указан</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const isInRoute = status === 'in_route';
        return (
          <Tag color={isInRoute ? 'green' : 'red'}>
            {isInRoute ? 'В маршруте' : 'В простое'}
          </Tag>
        );
      }
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 220,
      render: (_: any, record: CompanyDriver) => {
        const route = record.currentRoute;
        if (!route) return <Text type="secondary">—</Text>;

        const from = extractCityFromAddress(route.loadingAddress);
        const to = extractCityFromAddress(route.unloadingAddress);

        return (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 500 }}>
              <Button
                type="link"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/routes/${route.id}`);
                }}
                style={{ padding: 0, height: 'auto' }}
              >
                {from} → {to}
              </Button>
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>
              {route.routeNumber}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Заявка',
      key: 'application',
      width: 220,
      render: (_: any, record: CompanyDriver) => {
        if (!record.application) return <Text type="secondary">—</Text>;
        const app = record.application;
        return (
          <div style={{ fontSize: 12 }} onClick={(e) => e.stopPropagation()}>
            <ApplicationCell application={app} />
            <ExternalApplicationCell application={app} />
            {app.client?.name && (
              <div style={{ color: 'var(--text-secondary)' }}>{app.client.name}</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Дата погрузки',
      key: 'loadingDate',
      width: 140,
      render: (_: any, record: CompanyDriver) => {
        const route = record.currentRoute;
        const planned = route?.plannedLoadingDate;
        const actual = route?.actualLoadingDate;
        return (
          <div style={{ fontSize: 12 }}>
            <div>
              пл: {planned ? dayjs(planned).format('DD.MM.YY') : '—'}
            </div>
            <div>
              ф: {actual ? dayjs(actual).format('DD.MM.YY') : '—'}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Дата выгрузки',
      key: 'unloadingDate',
      width: 140,
      render: (_: any, record: CompanyDriver) => {
        const route = record.currentRoute;
        const planned = route?.plannedUnloadingDate;
        const actual = route?.actualUnloadingDate;
        return (
          <div style={{ fontSize: 12 }}>
            <div>
              пл: {planned ? dayjs(planned).format('DD.MM.YY') : '—'}
            </div>
            <div>
              ф: {actual ? dayjs(actual).format('DD.MM.YY') : '—'}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Логист',
      key: 'logist',
      width: 180,
      ellipsis: true,
      render: (_: any, record: CompanyDriver) => {
        const label = formatResponsibleUserLabel(resolveDriverLogist(record));
        if (label === '—') return <Text type="secondary">—</Text>;
        return <Text>{label}</Text>;
      }
    }
  ];

  const filteredDrivers = useMemo(() => {
    if (filterDriverId != null) {
      return drivers.filter((d) => d.id === filterDriverId);
    }
    if (activeTab === 'all') return drivers;
    return drivers.filter((d) => d.status === activeTab);
  }, [drivers, activeTab, filterDriverId]);

  return (
    <PageLayout
      title="Водители и транспорт"
      subtitle="Управление водителями и транспортом компании"
      actions={
        isAdmin ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenDriverCreate}>
            Новый водитель
          </Button>
        ) : undefined
      }
    >
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'all' | 'in_route' | 'idle')}
          type="card"
        >
          <TabPane
            tab={
              <span>
                Все ({drivers.length})
              </span>
            }
            key="all"
          />
          <TabPane
            tab={
              <span>
                В маршрутах ({drivers.filter(d => d.status === 'in_route').length})
              </span>
            }
            key="in_route"
          />
          <TabPane
            tab={
              <span>
                В простое ({drivers.filter(d => d.status === 'idle').length})
              </span>
            }
            key="idle"
          />
        </Tabs>

        <DataTable
          columns={columns}
          dataSource={filteredDrivers}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Всего: ${total}`
          }}
          onRow={(record) => ({
            onClick: () => {
              if (record.currentRoute) {
                navigate(`/routes/${record.currentRoute.id}`);
              }
            },
            style: {
              backgroundColor: record.status === 'in_route' ? 'rgba(82, 196, 26, 0.1)' : 'rgba(255, 77, 79, 0.1)',
              cursor: record.currentRoute ? 'pointer' : 'default',
              ...(filterDriverId != null && record.id === filterDriverId
                ? { boxShadow: 'inset 0 0 0 2px #1890ff' }
                : {})
            }
          })}
        />
      </Card>

      {driverModalOpen && (
        <CreateDriverModal
          isOpen={driverModalOpen}
          onClose={() => {
            setDriverModalOpen(false);
            setEditingDriver(null);
            if (filterDriverId != null) {
              openedDriverFromUrlRef.current = null;
              navigate('/drivers-vehicles', { replace: true });
            }
            refetch();
          }}
          driver={driverDetailsForModal ?? editingDriver}
          onDriverCreated={() => {
            refetch();
          }}
        />
      )}

      {vehicleModalOpen && (
        <CreateVehicleModal
          isOpen={vehicleModalOpen}
          onClose={() => {
            setVehicleModalOpen(false);
            setEditingVehicleId(null);
            refetch();
          }}
          vehicle={vehicleData || null}
          onVehicleCreated={() => {
            refetch();
          }}
        />
      )}
    </PageLayout>
  );
};

export default DriversVehiclesPage;
